import {animateByClass} from '../../Library/Animation.js';
import {fromString} from '../ToastMessage';

const BLOCKED_SELECTOR   = '[data-sending-blocked]';
const RECHECK_SELECTOR   = '[data-recheck-sending]';
const RESEND_SELECTOR    = '[data-resend-verification]';
const REMAINING_SELECTOR = '[data-resend-remaining]';
const CHECKING_CLASS     = 'is-checking';
const SHAKE_CLASS        = 'shake';
const WAITING_CLASS      = 'resend-waiting';

const animates = element => window.getComputedStyle(element).animationName !== 'none';

// Buttons already counting down, so re-running the sweep over a swapped-in region cannot start a
// second interval on one that survived the swap.
const counting = new WeakSet();

/**
 * Resolves on a rotation boundary so the spinner never stops part way round, and immediately when
 * nothing is animating — under prefers-reduced-motion an iteration would never arrive.
 */
const completeRotation = spinner => new Promise(resolve => {
    if (!animates(spinner)) {
        resolve();

        return;
    }

    spinner.addEventListener('animationiteration', () => resolve(), {once: true});
});

const recheck = async button => {
    if (button.classList.contains(CHECKING_CLASS)) {
        return;
    }

    const spinner = button.querySelector('i');

    button.classList.add(CHECKING_CLASS);

    let available = false;

    try {
        available = (await (await fetch(button.dataset.recheckSending)).json()).available;
    } finally {
        if (spinner) {
            await completeRotation(spinner);
        }

        button.classList.remove(CHECKING_CLASS);
    }

    const callout = button.closest(BLOCKED_SELECTOR);

    if (available) {
        document.querySelectorAll(BLOCKED_SELECTOR).forEach(blocked => blocked.remove());

        return;
    }

    // Still unverified, so say so with movement rather than silently doing nothing.
    if (callout) {
        animateByClass(callout, SHAKE_CLASS);
    }
};

/**
 * Counts the button down to its next allowed send. The server refuses early sends regardless; this
 * only stops the click being offered, so a reload mid-cooldown resumes from the remaining seconds.
 */
const countDown = button => {
    if (counting.has(button)) {
        return;
    }

    let remaining = Number(button.dataset.resendRemaining);

    counting.add(button);

    button.disabled = true;

    button.classList.add(WAITING_CLASS);

    const tick = () => {
        if (remaining <= 0) {
            window.clearInterval(timer);

            counting.delete(button);

            delete button.dataset.resendRemaining;

            button.classList.remove(WAITING_CLASS);

            button.textContent = button.dataset.resendLabel;
            button.disabled    = false;

            return;
        }

        button.textContent = button.dataset.resendWaiting.replace('%d', remaining);
        remaining -= 1;
    };

    const timer = window.setInterval(tick, 1_000);

    tick();
};

const resend = async button => {
    button.disabled = true;

    let sent = false;

    try {
        const response = await fetch(button.dataset.resendVerification, {method: 'POST'});

        sent = response.ok;

        fromString(await response.text());
    } finally {
        if (sent && Number(button.dataset.resendCooldown) > 0) {
            button.dataset.resendRemaining = button.dataset.resendCooldown;

            countDown(button);
        } else {
            button.disabled = false;
        }
    }
};

document.addEventListener('click', event => {
    const recheckButton = event.target.closest(RECHECK_SELECTOR);

    if (recheckButton) {
        return recheck(recheckButton);
    }

    const resendButton = event.target.closest(RESEND_SELECTOR);

    if (resendButton) {
        return resend(resendButton);
    }
});

/**
 * Server-rendered buttons arrive mid-cooldown, and almost none of them are present at load: the
 * banner and every blocked callout are swapped in by htmx afterwards. So this runs over each
 * swapped-in region as well, not just once over the document.
 */
export const startResendCountdowns = (root = document) => {
    if (typeof root.querySelectorAll !== 'function') {
        return;
    }

    if (root.matches?.(REMAINING_SELECTOR)) {
        countDown(root);
    }

    root.querySelectorAll(REMAINING_SELECTOR).forEach(button => countDown(button));
};

startResendCountdowns();

document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
        return;
    }

    document.querySelectorAll(RECHECK_SELECTOR).forEach(recheck);
});
